
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const BookingSection = () => {
  const navigate = useNavigate();
  const [selectedAge, setSelectedAge] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const ageGroups = [
    { value: '4-6', label: 'Ages 3-5 (Little Kickers)' },
    { value: '6-8', label: 'Ages 6-8 (Young Players)' },
    { value: '9-12', label: 'Ages 9-12 (Development)' },
    { value: '13-16', label: 'Ages 13-16 (Elite Training)' }
  ];

  const availableTimes = {
    'Monday': ['4:00 PM - 5:00 PM', '5:15 PM - 6:30 PM', '6:45 PM - 8:00 PM'],
    'Tuesday': ['4:00 PM - 5:00 PM', '5:15 PM - 6:30 PM', '6:45 PM - 8:00 PM'],
    'Wednesday': ['4:00 PM - 5:00 PM', '5:15 PM - 6:30 PM', '6:45 PM - 8:00 PM'],
    'Thursday': ['4:00 PM - 5:00 PM', '5:15 PM - 6:30 PM', '6:45 PM - 8:00 PM'],
    'Friday': ['4:00 PM - 5:00 PM', '5:15 PM - 6:30 PM', '6:45 PM - 8:00 PM'],
    'Saturday': ['9:00 AM - 10:00 AM', '10:15 AM - 11:30 AM', '11:45 AM - 1:00 PM', '1:15 PM - 2:30 PM'],
    'Sunday': ['10:00 AM - 11:00 AM', '11:15 AM - 12:30 PM', '12:45 PM - 2:00 PM']
  };

  const days = Object.keys(availableTimes);

  const handleBookSession = () => {
    if (selectedAge && selectedDay && selectedTime) {
      navigate('/register');
    }
  };

  return (
    <section className="py-16 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">Book Your Trial Session</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to get started? Book a free trial session and see why NextZen Soccer Academy is the perfect choice for your young athlete.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6">Quick Session Finder</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Select Age Group</label>
                  <div className="relative">
                    <select 
                      value={selectedAge}
                      onChange={(e) => setSelectedAge(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white/50 pr-8"
                    >
                      <option value="">Choose age group...</option>
                      {ageGroups.map((group) => (
                        <option key={group.value} value={group.value} className="text-gray-900">
                          {group.label}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-white pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Preferred Day</label>
                  <div className="relative">
                    <select 
                      value={selectedDay}
                      onChange={(e) => setSelectedDay(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white/50 pr-8"
                    >
                      <option value="">Choose day...</option>
                      {days.map((day) => (
                        <option key={day} value={day} className="text-gray-900">
                          {day}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-white pointer-events-none"></i>
                  </div>
                </div>

                {selectedDay && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Available Times</label>
                    <div className="relative">
                      <select 
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white/50 pr-8"
                      >
                        <option value="">Choose time...</option>
                        {availableTimes[selectedDay as keyof typeof availableTimes].map((time) => (
                          <option key={time} value={time} className="text-gray-900">
                            {time}
                          </option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-white pointer-events-none"></i>
                    </div>
                  </div>
                )}

                <button 
                  onClick={handleBookSession}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all whitespace-nowrap ${
                    selectedAge && selectedDay && selectedTime
                      ? 'bg-white text-blue-600 hover:bg-gray-100 cursor-pointer'
                      : 'bg-white/20 text-white/50 cursor-not-allowed'
                  }`}
                  disabled={!selectedAge || !selectedDay || !selectedTime}
                >
                  Book Free Trial Session
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              <div className="space-y-2">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto">
                  <i className="ri-gift-line text-xl text-white"></i>
                </div>
                <h4 className="font-semibold">Free Trial</h4>
                <p className="text-sm text-gray-300">First session completely free</p>
              </div>
              <div className="space-y-2">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <h4 className="font-semibold">No Commitment</h4>
                <p className="text-sm text-gray-300">Try before you commit</p>
              </div>
              <div className="space-y-2">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
                  <i className="ri-calendar-check-line text-xl text-white"></i>
                </div>
                <h4 className="font-semibold">Flexible</h4>
                <p className="text-sm text-gray-300">Easy rescheduling options</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <img 
              src="https://public.readdy.ai/ai/img_res/0244281f5fd9e6c9e344e7050c122704.jpg"
              alt="Booking and Registration"
              className="w-full h-80 object-cover rounded-xl shadow-lg"
            />
            
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-4">What to Expect</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-400 text-lg mt-0.5"></i>
                  <div>
                    <div className="font-medium">Skill Assessment</div>
                    <div className="text-sm text-gray-300">Our coaches will evaluate your child's current abilities</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-400 text-lg mt-0.5"></i>
                  <div>
                    <div className="font-medium">Fun Activities</div>
                    <div className="text-sm text-gray-300">Age-appropriate drills and games to keep kids engaged</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-400 text-lg mt-0.5"></i>
                  <div>
                    <div className="font-medium">Program Recommendation</div>
                    <div className="text-sm text-gray-300">Personalized advice on the best program for your child</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-400 text-lg mt-0.5"></i>
                  <div>
                    <div className="font-medium">Meet the Team</div>
                    <div className="text-sm text-gray-300">Introduction to coaches and academy philosophy</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
